import { forwardRef, InputHTMLAttributes, ReactNode, useId } from 'react';
import { cn } from '../../lib/cn';

/**
 * Input — campo de texto base.
 *
 * Acepta label, helper text y mensaje de error opcionales (modo standalone).
 * En formularios prefiramos envolver dentro del HOC `<Field>` (lo crea F4).
 * Las clases visuales se apoyan en los tokens del proyecto y respetan el
 * ring dorado global de foco.
 */
export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: ReactNode;
  helperText?: ReactNode;
  error?: ReactNode;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  /** Marca el control como invalido sin necesidad de pasar `error` explicito. */
  invalid?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      id,
      label,
      helperText,
      error,
      leftIcon,
      rightIcon,
      invalid,
      required,
      disabled,
      className,
      ...props
    },
    ref,
  ) => {
    const autoId = useId();
    const inputId = id ?? autoId;
    const helperId = `${inputId}-helper`;
    const errorId = `${inputId}-error`;
    const isInvalid = Boolean(error) || invalid === true;

    // aria-describedby combina helper y/o error si existen.
    const describedBy =
      [error ? errorId : null, helperText ? helperId : null].filter(Boolean).join(' ') || undefined;

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={inputId}
            className="block text-caption font-body font-semibold text-primary-900 mb-2"
          >
            {label}
            {required && (
              <span aria-hidden="true" className="ml-1 text-error">
                *
              </span>
            )}
            {required && <span className="sr-only"> (requerido)</span>}
          </label>
        )}

        <div
          className={cn(
            // Contenedor visual del control (permite leading/trailing icons).
            'group relative flex items-center w-full',
            'bg-cream-50 rounded',
            'border transition-colors duration-2 ease-out',
            isInvalid ? 'border-error' : 'border-cream-200 hover:border-primary-500',
            'focus-within:border-primary-700 focus-within:shadow-elev1',
            disabled && 'opacity-50 cursor-not-allowed',
          )}
        >
          {leftIcon && (
            <span
              className="pl-3 inline-flex items-center text-mute-500"
              aria-hidden="true"
            >
              {leftIcon}
            </span>
          )}

          <input
            ref={ref}
            id={inputId}
            required={required}
            disabled={disabled}
            aria-invalid={isInvalid || undefined}
            aria-describedby={describedBy}
            className={cn(
              'flex-1 bg-transparent text-body text-primary-900 placeholder:text-mute-500',
              'px-3 py-2 outline-none',
              // El ring dorado lo da :focus-visible global.
              'disabled:cursor-not-allowed',
              className,
            )}
            {...props}
          />

          {rightIcon && (
            <span
              className="pr-3 inline-flex items-center text-mute-500"
              aria-hidden="true"
            >
              {rightIcon}
            </span>
          )}
        </div>

        {error ? (
          <p id={errorId} role="alert" className="mt-2 text-caption text-error">
            {error}
          </p>
        ) : helperText ? (
          <p id={helperId} className="mt-2 text-caption text-primary-900/70">
            {helperText}
          </p>
        ) : null}
      </div>
    );
  },
);

Input.displayName = 'Input';
